import React from 'react';
import { Box, SortDirection, Typography } from '@material-ui/core';
import { format } from 'date-fns';

import { PaginatedWrappedComponentProps, withPagination } from 'features/app/hocs/withPagination';
import { OrderReport } from 'features/order-history/ts/OrderReport';
import { Table } from 'features/app/components/Table';
import { TableCellDef, TableRowActions } from 'features/app/ts/Table';
import { TableItemStatus } from 'features/app/components/TableItemStatus';
import { Tabs } from 'shared/tabs';
import { RangeDatePicker } from 'shared/date-pickers/RangeDatePicker';
import BaseButton from 'features/app/components/BaseButton';
import i18n, { t } from 'locales/i18n';
import { OrderReportFilters } from 'features/order-history/ts/OrderReportFilters';
import { useTranslation } from 'hooks/useTranslation';
import { getOrderReportCsvRequest, resendEmailRequest } from 'features/order-history/api';
import { FileUtils } from 'utils/FileUtils';
import { useStyles } from './styles';
import { ErrorUtils } from 'utils';
import { useMessage } from 'features/message/hooks/useMessage';
import { Search } from 'features/app/components/Search';
import { STATUS_DICTIONARY } from 'features/order-history/constants/statusDictionary';
import { orderByToOrdering, orderingToSortDirection } from 'features/order-history/utils/mapOrdering';

const columns = [
  { field: 'id', label: t('orderId', { ns: 'order-history' }) },
  { field: 'ordered_by', label: t('orderBy', { ns: 'order-history' }) },
  { field: 'date', label: t('date', { ns: 'order-history' }), sortable: true },
  { field: 'name', label: t('name', { ns: 'order-history' }) },
  { field: 'product', label: t('product', { ns: 'order-history' }) },
  { field: 'denomination', label: t('denomination', { ns: 'order-history' }) },
  { field: 'currency_code', label: t('columns.currency', { ns: 'redemptions' }) },
  { field: 'order_status', label: t('status', { ns: 'order-history' }) },
];

const cells: TableCellDef[] = [
  {
    field: 'id',
  },
  {
    field: 'ordered_by',
  },
  {
    field: 'date',
    component: ({ item }) => {
      const classes = useStyles();

      return (
        <Typography className={classes.tableItemDefaultStyle}>
          {format(new Date(item as string), 'dd/MM/yyyy')}
        </Typography>
      );
    },
  },
  {
    field: 'name',
  },
  {
    field: 'product',
  },
  {
    field: 'denomination',
    component: ({ item }) => {
      const classes = useStyles();

      return <Typography className={classes.denomination}>{Number(item).toFixed(2)}</Typography>;
    },
  },
  {
    field: 'currency_code',
  },
  {
    field: 'order_status',
    component: ({ item }) => {
      const getOrderStatus = () => {
        switch ((item as string)) {
          case 'DELIVERED':
            return i18n.t('tabs.delivered',{ ns: 'order-history' });
          case 'FAILED':
            return i18n.t('tabs.failed',{ ns: 'order-history' });
          case 'PROCESSING':
            return i18n.t('tabs.processing',{ ns: 'order-history' });
          default:
            return ''
        }
      }
      return (
        <TableItemStatus colorScheme={STATUS_DICTIONARY[item as OrderReport['order_status']]}>
          {getOrderStatus()}
        </TableItemStatus>
      )
    },
  },
];

const statusFilterTabs = [
  { value: '', label: t('tabs.all', { ns: 'order-history' }) },
  { value: 'DELIVERED', label: t('tabs.delivered', { ns: 'order-history' }) },
  { value: 'PROCESSING', label: t('tabs.processing', { ns: 'order-history' }) },
  { value: 'FAILED', label: t('tabs.failed', { ns: 'order-history' }) },
];

interface OrdersListProps {}

const ordersList = (props: OrdersListProps) => {
  const { filters, setFilters, data, isLoading, page, pageSize } = props as PaginatedWrappedComponentProps<
    OrderReport,
    OrderReportFilters
  >;

  const [isDownloading, setIsDownloading] = React.useState(false);
  const { t } = useTranslation('order-history');
  const classes = useStyles();
  const { showErrorMessage, showSuccessMessage } = useMessage();

  const handleOrderStatusChange = (_: unknown, value: OrderReport['order_status']) => {
    setFilters({ ...filters, order_status: value });
  };

  const createFilterChangeHandler = (filter: keyof OrderReportFilters) => {
    return (value: unknown) => {
      setFilters({ ...filters, [filter]: value });
    };
  };

  const handleDownloadClick = async () => {
    setIsDownloading(true);
    showSuccessMessage(t('downloadStartedMessage'));
    try {
      const { data } = await getOrderReportCsvRequest({ ...filters, page, page_size: pageSize });

      FileUtils.downloadFile(data);
      showSuccessMessage(t('reportDownloadedMessage'));
    } catch (error) {
      showErrorMessage(ErrorUtils.convertError(error));
    }

    setIsDownloading(false);
  };

  const rowActions: TableRowActions[] = React.useMemo(
    () => [
      {
        label: t('resendEmail', { ns: 'order-history' }),
        event: async item => {
          try {
            await resendEmailRequest((item as OrderReport).id);
            showSuccessMessage(t('resendEmail'));
          } catch (error) {
            showErrorMessage(ErrorUtils.convertError(error));
          }
        },
        shouldHide: ({ order_status }: OrderReport) => order_status !== 'DELIVERED',
      },
    ],
    [showSuccessMessage, showErrorMessage, t]
  );

  const handleSortChange = React.useCallback(
    (_: string, orderBy: SortDirection) => {
      setFilters({ ...filters, ordering: orderByToOrdering(orderBy) });
    },
    [filters, setFilters]
  );

  return (
    <Box className={classes.root}>
      <Box className={classes.filtersContainer}>
        <Box className={classes.filters}>
          <Box>
            <Tabs value={filters.order_status ?? ''} tabs={statusFilterTabs} onChange={handleOrderStatusChange} />
          </Box>
          <Box marginLeft={10} className={classes.searchWrapper}>
            <Search value={filters.search} onChange={createFilterChangeHandler('search')} />
          </Box>
        </Box>
        <Box className={classes.datePickerContainer}>
          <RangeDatePicker
            startDate={filters.created_at_after}
            endDate={filters.created_at_before}
            onEndDateChange={createFilterChangeHandler('created_at_before')}
            onStartDateChange={createFilterChangeHandler('created_at_after')}
          />
          <BaseButton className={classes.downloadButton} onClick={handleDownloadClick} isLoading={isDownloading}>
            {t('downloadReport')}
          </BaseButton>
        </Box>
      </Box>
      <Table
        columns={columns}
        cells={cells as TableCellDef[]}
        data={data ?? []}
        initialLoading={isLoading}
        empty={t('ordersNotFound')}
        rowsActions={rowActions}
        onSort={handleSortChange}
        orderBy="date"
        sortDirection={orderingToSortDirection(filters.ordering)}
      />
    </Box>
  );
};

export const OrdersList = withPagination(
  {
    pageSize: 8,
    getEndpoint: '/orders/own-history/',
    queryId: 'own-orders',
    defaultFilters: {
      ordering: '-created_at',
    },
  },
  ordersList
);
